import { useMergeRefs } from '@chakra-ui/react';
import { useRef, useEffect, useCallback, useMemo, forwardRef } from 'react';
import { useClickAway } from 'react-use';

import type { SuggestionSourceMetadata } from '@frontend/api';
import {
  Box,
  Input,
  InputGroup,
  Menu,
  MenuList,
  Portal,
  MenuButton,
} from '@frontend/components';

import {
  AssistanceAvailableBackground,
  AutoResizeTextarea,
  InputGroupWithSuggestionsRightElement,
  InputGroupWithSuggestionsProvider,
  SuggestionMenuItem,
} from './components';
import {
  useControllableOpen,
  useControllableValue,
  useMenuFocus,
  useOnKeyDown,
  useSuggestionDerivedState,
} from './hooks';

export type InputGroupWithSuggestionsInputType = 'input' | 'textarea';

export type FieldSuggestionProvenance = {
  filename: string;
  metadata: SuggestionSourceMetadata;
};

export type FieldSuggestionOption = {
  value: string;
  provenance: FieldSuggestionProvenance[];
};

export type InputGroupWithSuggestionsProps = {
  inputType?: InputGroupWithSuggestionsInputType;
  suggestions?: FieldSuggestionOption[];
  /** Controlled value — when provided, component displays this and calls onChange on input. */
  value?: string | null;
  /** Uncontrolled initial value. */
  defaultValue?: string | null;
  /** Called on every input change (required for controlled mode). */
  onChange?: (value: string | null) => void;
  /** Called when the user commits the value (blur, Enter, Tab, suggestion click). */
  onCommit?: (value: string | null) => void;
  isDisabled?: boolean;
  dataAttributes?: Record<string, boolean>;
  id?: string | number;
  name?: string;
  /** Controlled open state for the suggestions menu. */
  isOpen?: boolean;
  /** Called when the suggestions menu wants to open. */
  onOpen?: () => void;
  /** Called when the suggestions menu wants to close. */
  onClose?: () => void;
  /** When transitioning true→false, force-syncs internal value from defaultValue
   *  to handle server sanitization returning the same value. */
  isLoading?: boolean;
};

export const InputGroupWithSuggestions = forwardRef<
  HTMLDivElement,
  InputGroupWithSuggestionsProps
>(
  (
    {
      inputType = 'input',
      suggestions = [],
      value: valueProp,
      defaultValue: defaultValueProp,
      onChange: onChangeProp,
      onCommit,
      isDisabled = false,
      dataAttributes = {},
      name,
      id,
      isOpen: isOpenProp,
      onOpen: onOpenProp,
      onClose: onCloseProp,
      isLoading,
    },
    formControlRef,
  ) => {
    const inputGroupRef = useRef<HTMLDivElement>(null);
    const mergedInputGroupRef = useMergeRefs(formControlRef, inputGroupRef);

    const { localValue, setLocalValue } = useControllableValue({
      value: valueProp,
      defaultValue: defaultValueProp,
      onChange: onChangeProp,
      isLoading,
    });

    const {
      isOpen,
      isOpenControlled,
      handleOpen,
      handleClose: handleMenuClose,
    } = useControllableOpen({
      isOpen: isOpenProp,
      onOpen: onOpenProp,
      onClose: onCloseProp,
    });

    const suggestionsMenuRef = useRef<HTMLDivElement>(null);
    const inputElementRef = useRef<HTMLInputElement | HTMLTextAreaElement>(
      null,
    );

    const {
      acceptedSuggestionIndex,
      defaultOptionContent,
      calculatedRows,
      hasSuggestions,
      acceptedSuggestion,
      availableSuggestions,
    } = useSuggestionDerivedState({ localValue, suggestions });

    const {
      menuFocus,
      setMenuFocus,
      handleSuggestionHover,
      handleMouseLeaveMenu,
    } = useMenuFocus({
      isOpen,
      localValue,
      acceptedCaseInsensitiveIndex: acceptedSuggestionIndex.caseInsensitive,
      suggestionsLength: suggestions.length,
    });

    const handleClose = useCallback(
      (submitValue?: string | null) => {
        handleMenuClose();
        onCommit?.(
          submitValue !== undefined
            ? submitValue
            : localValue
            ? localValue.trim()
            : localValue,
        );
      },
      [localValue, onCommit, handleMenuClose],
    );

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setLocalValue(e.target?.value ?? '');
      },
      [setLocalValue],
    );

    const handleSuggestionSelect = useCallback(
      (value: string) => {
        setLocalValue(value);
        handleClose(value);
      },
      [setLocalValue, handleClose],
    );

    const onKeyDown = useOnKeyDown({
      isOpen,
      menuFocus,
      suggestions,
      defaultOptionContentValue: defaultOptionContent.value,
      handleClose,
      handleOpen,
      setMenuFocus,
      setLocalValue,
    });

    useClickAway(inputGroupRef, (e) => {
      if (!isOpen) return;
      if (suggestionsMenuRef.current?.contains(e.target as Node)) return;
      handleClose();
    });

    // When open state is externally controlled, focus the input on open
    useEffect(() => {
      if (isOpenControlled && isOpen && inputElementRef.current) {
        inputElementRef.current.focus();
      }
    }, [isOpenControlled, isOpen]);

    const handleBlur = useCallback(() => {
      if (isOpen) return;
      onCommit?.(localValue?.trim() ?? null);
    }, [isOpen, onCommit, localValue]);

    const contextValue = useMemo(
      () => ({
        acceptedSuggestion,
        availableSuggestions,
        isDisabled,
      }),
      [acceptedSuggestion, availableSuggestions, isDisabled],
    );

    const commonProps = {
      name,
      id: id?.toString(),
      ...(!hasSuggestions && dataAttributes),
      onFocus: hasSuggestions ? handleOpen : undefined,
      onChange: handleInputChange,
      onKeyDown: hasSuggestions ? onKeyDown : undefined,
      onBlur: handleBlur,
      value: localValue ?? '',
      pr: hasSuggestions ? 4 : 1,
      color: 'inherit',
      borderColor: hasSuggestions ? 'inherit' : undefined,
      'data-fs-hide': 'true',
    };

    const inputElement =
      inputType === 'input' ? (
        <Input
          ref={inputElementRef as React.RefObject<HTMLInputElement>}
          {...commonProps}
          autoComplete="off"
        />
      ) : (
        <AutoResizeTextarea
          ref={inputElementRef as React.RefObject<HTMLTextAreaElement>}
          {...commonProps}
          h="auto"
          resize="none"
          overflow="hidden"
          minH={4}
          rows={calculatedRows}
        />
      );

    if (!hasSuggestions) {
      return <InputGroup ref={mergedInputGroupRef}>{inputElement}</InputGroup>;
    }

    return (
      <InputGroupWithSuggestionsProvider value={contextValue}>
        <Menu isOpen={isOpen} placement="bottom-end">
          <InputGroup
            ref={mergedInputGroupRef}
            color={acceptedSuggestion ? 'interaction.suggestion' : undefined}
            borderColor={
              availableSuggestions && !isDisabled
                ? 'interaction.suggestion'
                : 'separator.border'
            }
            overflow="hidden"
          >
            {inputElement}
            {availableSuggestions && !isDisabled && (
              <AssistanceAvailableBackground />
            )}
            <InputGroupWithSuggestionsRightElement />
          </InputGroup>
          <MenuButton as={Box} />
          <Portal>
            <MenuList
              minW={inputGroupRef?.current?.offsetWidth ?? '300px'}
              maxW={`max(${
                inputGroupRef?.current?.offsetWidth || '0'
              }px, 300px)`}
              maxH="300px"
              overflowY="auto"
              ref={suggestionsMenuRef}
              onMouseLeave={handleMouseLeaveMenu}
            >
              {suggestions.map((suggestion, ind) => (
                <SuggestionMenuItem
                  key={suggestion.value}
                  index={ind}
                  value={suggestion.value}
                  provenance={suggestion.provenance}
                  isFocused={menuFocus === ind}
                  onSelect={handleSuggestionSelect}
                  onHover={handleSuggestionHover}
                />
              ))}
              <SuggestionMenuItem
                index={suggestions.length}
                value={defaultOptionContent.label}
                selectValue={defaultOptionContent.value}
                isRejected={!defaultOptionContent.value || !acceptedSuggestion}
                isFocused={menuFocus === suggestions.length}
                onSelect={handleSuggestionSelect}
                onHover={handleSuggestionHover}
              />
            </MenuList>
          </Portal>
        </Menu>
      </InputGroupWithSuggestionsProvider>
    );
  },
);
InputGroupWithSuggestions.displayName = 'InputGroupWithSuggestions';
